import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import '../components/home/HomeStatusControls.css';
import { useToasts } from '../ui/ToastProvider';
import { ensureQuoteChatReads } from '../lib/quoteChatReads';
import { useRoles } from '../hooks/useRoles';
import {
  DEEP_REQUESTS_QUERY_KEY,
  deepRequestsRefetchInterval,
  fetchDeepRequests,
  approveDeepResearch,
  denyDeepResearch,
  type DeepResearchRequest,
} from '../data/deepResearch';
import useSessionStore from '../state/session';
import { sendQuickChatMessage, updateMentionStatus } from '../data/orderChat';
import {
  markNotificationRead,
  dashboardNotificationActionUrl,
  dismissNotification,
  recallBroadcast,
  dismissBroadcast,
  dismissTask,
  type TaskDismissDuration,
  reopenTask,
  decideTransfer,
  decideEventConfirmationRequest,
  type DashboardPayload,
  type DashboardNotification,
  type DashboardBroadcast,
  type DashboardTask,
  type DashboardMention,
  type DashboardTransferRequest,
} from '../api/dashboard';
import { useDashboardQuery, dashboardQueryKey, usePendingApprovalsQuery, usePendingAgreementApprovalsQuery, useWorkspaceStatsQuery, pendingApprovalsQueryKey } from '../domain/home/useHomeDashboard';
import { useShellLayout } from '../ui/ShellLayoutContext';
import LayoutFeature from '../ui/LayoutFeature';
import { dismissApprovalAssignment } from '../data/home';
import { useEvent, useEventDashboardCounts } from '../domain/events/useEvents';
import JobTimeButton from '../components/time/JobTimeButton';
import SimpleInOutStatusCard from '../components/integrations/SimpleInOutStatusCard';
import SimpleInOutDotToggle from '../components/integrations/SimpleInOutDotToggle';
import HomeJobCaptureAction from '../components/home/HomeJobCaptureAction';
import ItemAiLookupReviewModal from '../components/item/ItemAiLookupReviewModal';
import { GuideQuestionButton, GuideTourOverlay, useGuideTour } from '../components/guides/GuideTour';
import { HOME_GUIDE } from '../components/guides/pageGuideContent';
import { useUnreadChatCounts } from '../hooks/useUnreadChatCounts';
import { invalidateAvailabilityQueries } from '../hooks/mutations/useInventoryMutations';
import { fetchConversationParticipants, sendConversationMessage, type ConversationRow } from '../data/chat';
import type { ChatProfile } from '../data/orderChat';
import { decideYearlyInventoryRequestRecall } from '../api/yearlyInventory';
import { completeHomeTask } from '../domain/home/taskActions';
import HomeTaskDismissButton from '../components/home/HomeTaskDismissButton';
import PendingApprovalCard from '../components/home/PendingApprovalCard';
import { useAgreementApprovalViewer } from '../hooks/useAgreementApprovals';

const FreelancerHomeDashboard = React.lazy(() => import('./FreelancerHomeDashboard'));
const GuestSharedDashboard = React.lazy(() => import('./GuestSharedDashboard'));
const EventLeaderboardModal = React.lazy(() => import('../components/event/EventLeaderboardModal'));
const HomePriceAvailabilityModal = React.lazy(() => import('../components/home/HomePriceAvailabilityModal'));
type PendingMap = Record<string, boolean>;

interface HomeDashboardPageProps {
  isAdmin: boolean;
  isManager: boolean;
  isSuperAdmin: boolean;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString();
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString();
}

function summariseBody(body: string | null | undefined, limit = 140): string {
  if (!body) return '--';
  const trimmed = body.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, limit - 1)}...`;
}

function getYearlyRecallRequestId(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata || typeof metadata !== 'object') return '';
  if (String(metadata.type || '') !== 'yearly_inventory_recall_request') return '';
  return String(metadata.requestId || '').trim();
}

function getEventConfirmationRequestId(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata || typeof metadata !== 'object') return '';
  if (String(metadata.type || '').trim().toLowerCase() !== 'event_confirmation_bypass_request') return '';
  return String(
    metadata.requestId
    || metadata.request_id
    || metadata.confirmationRequestId
    || metadata.confirmation_request_id
    || ''
  ).trim();
}

function getItemAiLookupRunId(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata || typeof metadata !== 'object') return '';
  if (String(metadata.type || '') !== 'item_ai_lookup') return '';
  return String(metadata.runId || metadata.run_id || metadata.jobId || metadata.job_id || '').trim();
}

function getSuggestionCategoryLabel(metadata: Record<string, unknown> | null | undefined): string | null {
  const category = String(metadata?.suggestionCategory || metadata?.suggestion_category || '').trim().toLowerCase();
  if (category === 'bug') return 'Bug';
  if (category === 'question') return 'Question';
  if (category === 'suggestion') return 'Suggestion';
  if (category === 'data_issue') return 'Data issue';
  return null;
}

const severityStyles: Record<string, { bg: string; fg: string }> = {
  info: { bg: 'var(--info-bg)', fg: 'var(--info-fg)' },
  success: { bg: 'var(--success-bg)', fg: 'var(--success-fg)' },
  warning: { bg: 'var(--warn-bg)', fg: 'var(--warn-fg)' },
  critical: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)' },
};

export default function HomePage() {
  const { isAdmin, isManager, isSuperAdmin, isFreelancer, securityLevel } = useRoles();
  if (isFreelancer) {
    return (
      <React.Suspense fallback={<div className="section-shell"><p className="text-sm fg-muted">Loading...</p></div>}>
        <FreelancerHomeDashboard />
      </React.Suspense>
    );
  }
  if (securityLevel === 'guest') {
    return (
      <React.Suspense fallback={<div className="section-shell"><p className="text-sm fg-muted">Loading...</p></div>}>
        <GuestSharedDashboard />
      </React.Suspense>
    );
  }
  return <HomeDashboardPage isAdmin={isAdmin} isManager={isManager} isSuperAdmin={isSuperAdmin} />;
}

function HomeDashboardPage({ isAdmin, isManager, isSuperAdmin }: HomeDashboardPageProps) {
  const { canViewPage, canEditPage, canEditFeature } = useRoles();
  const canViewEvents = canViewPage('events');
  const canViewCustomers = canViewPage('clients');
  const canViewVenues = canViewPage('venues');
  const canViewUsers = canViewPage('users');
  const canViewSchedule = canViewPage('schedule');
  const canUseInventoryTools = canViewPage('inventory') || canViewPage('admin_inventory');
  const canUseServiceScan = canViewPage('service') && canEditFeature('service.scan', 'own');
  const { push } = useToasts();
  const navigate = useNavigate();
  const sessionReady = useSessionStore(s => s.sessionReady);
  const session = useSessionStore(s => s.session);
  const queryClient = useQueryClient();
  const [pendingMap, setPendingMap] = React.useState<PendingMap>({});
  const [lastEventId, setLastEventId] = React.useState<string | null>(null);
  const profile = useSessionStore(s => s.profile);
  const profileId = useSessionStore(s => s.profileId);
  const dashboardQuery = useDashboardQuery({ enabled: sessionReady });
  const dashboard = dashboardQuery.data ?? null;
  const loading = dashboardQuery.isLoading;
  const error = (dashboardQuery.error as any)?.message || null;
  const statsQuery = useWorkspaceStatsQuery({
    enabled: sessionReady && (canViewEvents || canViewCustomers || canViewVenues || canViewUsers),
    includeEvents: canViewEvents,
    includeCustomers: canViewCustomers,
    includeVenues: canViewVenues,
    includeUsers: canViewUsers,
  });
  const stats = statsQuery.data ?? { events: 0, customers: 0, venues: 0, users: 0 };
  const statsLoading = statsQuery.isLoading;
  const eventCountsQuery = useEventDashboardCounts({ enabled: sessionReady && canViewEvents });
  const eventCounts = eventCountsQuery.data;
  const eventCountsLoading = eventCountsQuery.isLoading;
  const [showLeaderboard, setShowLeaderboard] = React.useState(false);
  const [showPriceAvailabilityModal, setShowPriceAvailabilityModal] = React.useState(false);
  const [itemReviewRunId, setItemReviewRunId] = React.useState<string | null>(null);
  const homeOverviewGuideRef = React.useRef<HTMLDivElement | null>(null);
  const homeSimpleInOutGuideRef = React.useRef<HTMLDivElement | null>(null);
  const homeStatsGuideRef = React.useRef<HTMLDivElement | null>(null);
  const homeAttentionGuideRef = React.useRef<HTMLDivElement | null>(null);
  const homeQuickActionsGuideRef = React.useRef<HTMLDivElement | null>(null);
  const homeGuideButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const approvalsQuery = usePendingApprovalsQuery({
    profileId,
    enabled: sessionReady && canViewEvents && Boolean(profileId),
  });
  const approvals = approvalsQuery.data?.items ?? [];
  const approvalsCount = approvalsQuery.data?.total ?? approvals.length;
  const approvalsLoading = approvalsQuery.isLoading;
  const approvalsError = (approvalsQuery.error as any)?.message || null;
  // Service agreement quotes waiting on this manager; they open on the quote's own page.
  const agreementApprovalViewer = useAgreementApprovalViewer();
  const agreementApprovalsQuery = usePendingAgreementApprovalsQuery({
    viewer: agreementApprovalViewer,
    enabled: sessionReady,
  });
  const agreementApprovals = agreementApprovalsQuery.data ?? [];
  const quoteApprovalsCount = approvalsCount + agreementApprovals.length;
  const quoteApprovalsShown = approvals.length + agreementApprovals.length;
  const quoteApprovalsLoading = approvalsLoading || agreementApprovalsQuery.isLoading;
  const quoteApprovalsError = approvalsError || agreementApprovalsQuery.error?.message || null;
  // Deep research approvals (visible to admins, managers, super admins)
  const canSeeDeepApprovals = !!session && canViewEvents && (isAdmin || isManager || isSuperAdmin);
  const deepRequestsQuery = useQuery({
    queryKey: DEEP_REQUESTS_QUERY_KEY,
    queryFn: () => fetchDeepRequests(session?.access_token),
    enabled: canSeeDeepApprovals && sessionReady,
    refetchInterval: deepRequestsRefetchInterval,
  });
  const deepRequests = deepRequestsQuery.data ?? [];
  // Unread chat conversations
  const { totalUnread: chatUnread, counts: chatCounts, conversations: chatConversations, markConversationRead, dismissConversation, dismissAllConversations } = useUnreadChatCounts();
  const [chatParticipantsMap, setChatParticipantsMap] = React.useState<Record<string, ChatProfile[]>>({});

  // Fetch participants for conversations with unread messages so we can show names
  React.useEffect(() => {
    const unreadConvIds = chatConversations
      .filter((c) => (chatCounts[c.conversation_id] ?? 0) > 0)
      .map((c) => c.conversation_id);
    if (unreadConvIds.length === 0) return;
    let cancelled = false;
    Promise.all(
      unreadConvIds.map(async (convId) => {
        const { data } = await fetchConversationParticipants(convId);
        const profiles = (data ?? [])
          .map((p) => p.profile)
          .filter((p): p is ChatProfile => p !== null);
        return { convId, profiles };
      }),
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, ChatProfile[]> = {};
      for (const { convId, profiles } of results) map[convId] = profiles;
      setChatParticipantsMap(map);
    });
    return () => {
      cancelled = true;
    };
  }, [chatConversations, chatCounts]);

  // Inline quick-reply state per mention id
  const [mentionReplyMap, setMentionReplyMap] = React.useState<Record<string, { open: boolean; text: string }>>({});
  const [conversationReplyMap, setConversationReplyMap] = React.useState<Record<string, { open: boolean; text: string }>>({});
  const [confirmationDecisionNotes, setConfirmationDecisionNotes] = React.useState<Record<string, string>>({});

  const setBusy = React.useCallback((key: string, value: boolean) => {
    setPendingMap(prev => {
      if (value) {
        if (prev[key]) return prev;
        return { ...prev, [key]: true };
      }
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isBusy = React.useCallback((key: string) => Boolean(pendingMap[key]), [pendingMap]);

  const updateDashboardData = React.useCallback(
    (updater: (current: DashboardPayload | null) => DashboardPayload | null) => {
      queryClient.setQueryData<DashboardPayload | null>(dashboardQueryKey, prev => updater(prev ?? null));
    },
    [queryClient],
  );

  React.useEffect(() => {
    if (!sessionReady) return; // wait until context (roles/locations) is ready
    void dashboardQuery.refetch();
  }, [dashboardQuery.refetch, sessionReady]);

  // Restore "Last viewed" event from localStorage and resolve its name/code
  React.useEffect(() => {
    if (!sessionReady || !canViewEvents) {
      setLastEventId(null);
      return;
    }
    try {
      const id = typeof window !== 'undefined' ? window.localStorage.getItem('last_event_id') : null;
      setLastEventId(id || null);
    } catch {
      setLastEventId(null);
    }
  }, [canViewEvents, sessionReady]);
  const lastEventQuery = useEvent({ eventId: lastEventId, enabled: canViewEvents && Boolean(lastEventId) });
  const lastEvent = lastEventQuery.data
    ? {
        id: lastEventQuery.data.id,
        name: (lastEventQuery.data as any).name ?? null,
        event_code: (lastEventQuery.data as any).event_code ?? (lastEventQuery.data as any).code ?? null,
      }
    : null;

  const greeting = React.useMemo(() => {
    const now = new Date();
    const h = now.getHours();
    if (h < 4) return 'Good night';
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    if (h < 22) return 'Good evening';
    return 'Good night';
  }, []);

  const displayName = React.useMemo(() => {
    const p = profile;
    if (!p) return 'there';
    if (p.first_name) return p.first_name;
    if (p.full_name) return p.full_name.split(' ')[0] || p.full_name;
    return p.email || 'there';
  }, [profile]);
  const chatDisplayName = React.useMemo(() => {
    if (!profile) return null;
    const full = profile.full_name?.trim();
    if (full) return full;
    const combined = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();
    if (combined) return combined;
    return profile.email?.trim() || null;
  }, [profile]);
  const homeGuide = useGuideTour({
    steps: HOME_GUIDE.steps,
    prefPath: HOME_GUIDE.prefPath,
    storageKey: HOME_GUIDE.storageKey,
    version: HOME_GUIDE.version,
    enabled: sessionReady,
    ready: sessionReady,
    autoOpen: true,
    resolveTarget: React.useCallback((target: string) => {
      if (target === 'overview') return homeOverviewGuideRef.current;
      if (target === 'simpleInOut') return homeSimpleInOutGuideRef.current;
      if (target === 'stats') return homeStatsGuideRef.current;
      if (target === 'attention') return homeAttentionGuideRef.current;
      if (target === 'quickActions') return homeQuickActionsGuideRef.current;
      if (target === 'guideButton') return homeGuideButtonRef.current;
      return null;
    }, []),
  });

  const notifications = dashboard?.notifications ?? { total: 0, items: [] as DashboardNotification[] };
  const broadcasts = dashboard?.broadcasts?.items ?? ([] as DashboardBroadcast[]);
  const tasks = dashboard?.tasks ?? { total: 0, items: [] as DashboardTask[] };
  // A test layout's "today at a glance" (docs/test-layouts.md, "Page features"), from counts already loaded.
  const { features: layoutFeatures, kit: layoutKit } = useShellLayout();
  const HomeGlance = layoutFeatures.homeGlance ? layoutKit?.TodayGlance : undefined;
  function countUnreadNotifications(items: DashboardNotification[]): number {
    return items.filter((item) => !item.readAt && !item.dismissedAt).length;
  }
  const mentions = dashboard?.mentions ?? { unread: 0, items: [] as DashboardMention[] };
  const transfers = dashboard?.transfers ?? { pending: 0, items: [] as DashboardTransferRequest[] };

  const handleNotificationRead = React.useCallback(async (notification: DashboardNotification) => {
    if (!notification || notification.readAt) return;
    const key = `notif-read-${notification.id}`;
    setBusy(key, true);
    try {
      await markNotificationRead(notification.id);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.notifications.items.map(item => item.id === notification.id
          ? { ...item, readAt: new Date().toISOString() }
          : item);
        return { ...prev, notifications: { ...prev.notifications, items } };
      });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update notification' });
    } finally {
      setBusy(key, false);
    }
  }, [push, setBusy, updateDashboardData]);

  const handleNotificationDismiss = React.useCallback(async (notification: DashboardNotification) => {
    const key = `notif-dismiss-${notification.id}`;
    setBusy(key, true);
    try {
      await dismissNotification(notification.id);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.notifications.items.filter(item => item.id !== notification.id);
        const total = Math.max(0, prev.notifications.total - 1);
        return { ...prev, notifications: { total, items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to dismiss notification' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleRecallBroadcast = React.useCallback(async (b: DashboardBroadcast) => {
    const key = `broadcast-recall-${b.id}`;
    setBusy(key, true);
    try {
      await recallBroadcast(b.id);
      updateDashboardData(prev => {
        if (!prev?.broadcasts?.items) return prev;
        const items = prev.broadcasts.items.filter(item => item.id !== b.id);
        return { ...prev, broadcasts: { items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      push({ type: 'success', message: 'Broadcast recalled' });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to recall broadcast' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleDismissBroadcast = React.useCallback(async (b: DashboardBroadcast) => {
    const key = `broadcast-dismiss-${b.id}`;
    setBusy(key, true);
    try {
      await dismissBroadcast(b.id);
      updateDashboardData(prev => {
        if (!prev?.broadcasts?.items) return prev;
        const items = prev.broadcasts.items.filter(item => item.id !== b.id);
        return { ...prev, broadcasts: { items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to dismiss broadcast' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleApproveDeep = React.useCallback(async (jobId: string) => {
    const key = `deep-approve-${jobId}`;
    setBusy(key, true);
    try {
      const { ok } = await approveDeepResearch(jobId);
      if (ok) {
        queryClient.setQueryData<DeepResearchRequest[]>(DEEP_REQUESTS_QUERY_KEY, (prev) =>
          (prev ?? []).filter((it) => it.job_id !== jobId),
        );
        push({ type: 'success', message: 'Deep research approved' });
      } else {
        push({ type: 'error', message: 'Approval failed' });
      }
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Approval failed' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy]);

  const handleDenyDeep = React.useCallback(async (jobId: string) => {
    const key = `deep-deny-${jobId}`;
    setBusy(key, true);
    try {
      const { ok } = await denyDeepResearch(jobId);
      if (ok) {
        queryClient.setQueryData<DeepResearchRequest[]>(DEEP_REQUESTS_QUERY_KEY, (prev) =>
          (prev ?? []).filter((it) => it.job_id !== jobId),
        );
        push({ type: 'success', message: 'Deep research denied' });
      } else {
        push({ type: 'error', message: 'Deny failed' });
      }
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Deny failed' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy]);

  const handleTaskComplete = React.useCallback(async (task: DashboardTask) => {
    const key = `task-complete-${task.id}`;
    setBusy(key, true);
    try {
      await completeHomeTask(task);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.tasks.items.filter(item => item.id !== task.id);
        const total = Math.max(0, prev.tasks.total - 1);
        return { ...prev, tasks: { total, items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['user-todo-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['planner-open-suggestions'] });
      void queryClient.invalidateQueries({ queryKey: ['planner-submitted-suggestions'] });
      push({ type: 'success', message: 'Task completed' });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to complete task' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleTaskDismiss = React.useCallback(async (task: DashboardTask, duration: TaskDismissDuration) => {
    const key = `task-dismiss-${task.id}`;
    setBusy(key, true);
    try {
      await dismissTask(task.id, duration);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.tasks.items.filter(item => item.id !== task.id);
        return { ...prev, tasks: { total: Math.max(0, prev.tasks.total - 1), items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      push({ type: 'success', message: duration === 'forever'
        ? 'Removed from Home. The task is still open.'
        : `Dismissed for 1 ${duration}. The task is still open.` });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to dismiss task' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleTaskReopen = React.useCallback(async (task: DashboardTask) => {
    const key = `task-reopen-${task.id}`;
    setBusy(key, true);
    try {
      await reopenTask(task.id);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.tasks.items.map(item => item.id === task.id
          ? { ...item, status: 'open', metadata: { ...item.metadata }, }
          : item);
        return { ...prev, tasks: { ...prev.tasks, items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to reopen task' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleTransferDecision = React.useCallback(async (transfer: DashboardTransferRequest, decision: 'approve' | 'reject') => {
    const key = `transfer-${decision}-${transfer.id}`;
    setBusy(key, true);
    try {
      await decideTransfer(transfer.id, decision);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.transfers.items.filter(item => item.id !== transfer.id);
        const pending = Math.max(0, prev.transfers.pending - 1);
        return { ...prev, transfers: { pending, items } };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update transfer request' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleRecallTaskDecision = React.useCallback(async (task: DashboardTask, decision: 'approved' | 'denied') => {
    const requestId = getYearlyRecallRequestId(task.metadata);
    if (!requestId) {
      push({ type: 'error', message: 'Recall request is missing its request id.' });
      return;
    }
    const key = `recall-${decision}-${task.id}`;
    setBusy(key, true);
    try {
      await decideYearlyInventoryRequestRecall(requestId, decision, task.id);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.tasks.items.filter(item => item.id !== task.id);
        const total = Math.max(0, prev.tasks.total - 1);
        const notificationItems = prev.notifications.items.filter((item) => (
          getYearlyRecallRequestId(item.metadata) !== requestId
        ));
        const notificationTotal = Math.max(0, prev.notifications.total - (prev.notifications.items.length - notificationItems.length));
        return {
          ...prev,
          tasks: { total, items },
          notifications: { total: notificationTotal, items: notificationItems },
        };
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      push({
        type: 'success',
        message: decision === 'approved' ? 'Recall approved' : 'Recall denied',
      });
    } catch (err) {
      push({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update recall request' });
    } finally {
      setBusy(key, false);
    }
  }, [push, queryClient, setBusy, updateDashboardData]);

  const handleEventConfirmationDecision = React.useCallback(async (
    task: DashboardTask,
    decision: 'approved' | 'denied',
  ) => {
    const requestId = getEventConfirmationRequestId(task.metadata);
    if (!requestId) {
      push({ type: 'error', message: 'Confirmation request is missing its request id.' });
      return;
    }
    const note = String(confirmationDecisionNotes[task.id] || '').trim();
    if (decision === 'denied' && !note) {
      push({ type: 'error', message: 'Enter a decision note before denying this request.' });
      return;
    }
    const key = `event-confirmation-${decision}-${task.id}`;
    setBusy(key, true);
    try {
      const result = await decideEventConfirmationRequest(requestId, decision, note || null);
      updateDashboardData(prev => {
        if (!prev) return prev;
        const items = prev.tasks.items.filter((item) => (
          getEventConfirmationRequestId(item.metadata) !== requestId
        ));
        const removedTaskCount = prev.tasks.items.length - items.length;
        const notificationItems = prev.notifications.items.filter((item) => (
          getEventConfirmationRequestId(item.metadata) !== requestId
        ));
        const removedNotificationCount = prev.notifications.items.length - notificationItems.length;
        return {
          ...prev,
          tasks: {
            total: Math.max(0, prev.tasks.total - removedTaskCount),
            items,
          },
          notifications: {
            total: Math.max(0, prev.notifications.total - removedNotificationCount),
            items: notificationItems,
          },
        };
      });
      setConfirmationDecisionNotes((prev) => {
        if (!(task.id in prev)) return prev;
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      const outcome = String(result.outcome || '').trim().toLowerCase();
      if (result.eventId) {
        void queryClient.invalidateQueries({
          queryKey: ['event-confirmation', result.eventId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['order-stage', result.eventId],
        });
        if (outcome === 'confirmed' || outcome === 'already_confirmed') {
          invalidateAvailabilityQueries(queryClient);
        }
      }
      window.dispatchEvent(new CustomEvent('dashboard:notifications-refresh'));
      if (outcome === 'already_confirmed') {
        push({ type: 'info', message: 'The event was already confirmed through another valid path.' });
      } else if (outcome === 'stale_request') {
        push({ type: 'info', message: 'This request became stale and was closed without confirming the event.' });
      } else {
        push({
          type: 'success',
          message: decision === 'approved'
            ? 'Event confirmation approved'
            : 'Event confirmation denied',
        });
      }
    } catch (err) {
      push({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to decide event confirmation request',
      });
    } finally {
      setBusy(key, false);
    }
  }, [
    confirmationDecisionNotes,
    push,
    queryClient,
    setBusy,
    updateDashboardData,
  ]);

  const hasNotificationsOrBroadcasts = notifications.items.length > 0 || broadcasts.length > 0;

  const renderNotifications = () => {
    if (loading && !hasNotificationsOrBroadcasts) {
      return <div className="text-sm fg-muted">Loading notifications...</div>;
    }
    if (!hasNotificationsOrBroadcasts) {
      return <div className="text-sm fg-muted">You're all caught up. New notifications will appear here.</div>;
    }
    return (
      <div className="stack gap-sm">
        {broadcasts.map((b: DashboardBroadcast) => (
          <div
            key={b.id}
            className="stack gap-xs"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '0.85rem 1rem',
              background: 'var(--surface-soft)',
            }}
          >
            <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="stack gap-xxs" style={{ minWidth: 0 }}>
                <div className="text-sm" style={{ fontWeight: 600 }}>{b.title}</div>
                {b.body && (
                  <div className="text-xs fg-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {summariseBody(b.body)}
                  </div>
                )}
              </div>
              <span className="pill micro" style={{ background: 'var(--info-bg)', color: 'var(--info-fg)' }}>
                BROADCAST
              </span>
            </div>
            <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div className="text-xxs fg-muted">
                {b.createdByName ?? 'Admin'} | {formatDateTime(b.createdAt)}
              </div>
              <div className="flex" style={{ gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn xs subtle"
                  onClick={() => void handleDismissBroadcast(b)}
                  disabled={isBusy(`broadcast-dismiss-${b.id}`)}
                >
                  {isBusy(`broadcast-dismiss-${b.id}`) ? 'Dismissing…' : 'Dismiss'}
                </button>
                {b.canRecall && (
                  <button
                    type="button"
                    className="btn xs subtle"
                    onClick={() => void handleRecallBroadcast(b)}
                    disabled={isBusy(`broadcast-recall-${b.id}`)}
                  >
                    {isBusy(`broadcast-recall-${b.id}`) ? 'Recalling…' : 'Recall'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {notifications.items.map((notification: DashboardNotification) => {
          const severity = severityStyles[String(notification.severity || 'info').toLowerCase()] || severityStyles.info;
          const itemAiRunId = getItemAiLookupRunId(notification.metadata);
          return (
            <div
              key={notification.id}
              className="stack gap-xs"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                background: 'var(--surface-soft)',
              }}
            >
              <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                <div className="stack gap-xxs" style={{ minWidth: 0 }}>
                  <div className="text-sm" style={{ fontWeight: 600 }}>{notification.title}</div>
                  <div className="text-xs fg-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {summariseBody(notification.body)}
                  </div>
                </div>
                <span
                  className="pill micro"
                  style={{ background: severity.bg, color: severity.fg }}
                >
                  {String(notification.severity || 'info').toUpperCase()}
                </span>
              </div>
              
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="text-xxs fg-muted">
                  {notification.locationName ? `For ${notification.locationName}` : 'Personal'} | {formatDateTime(notification.createdAt)}
                </div>
                <div className="flex" style={{ gap: '0.5rem' }}>
                  {!notification.readAt && (
                    <button
                      type="button"
                      className="btn xs subtle"
                      onClick={() => void handleNotificationRead(notification)}
                      disabled={isBusy(`notif-read-${notification.id}`)}
                    >
                      {isBusy(`notif-read-${notification.id}`) ? 'Marking...' : 'Mark read'}
                    </button>
                  )}
                  {itemAiRunId && (
                    <button
                      type="button"
                      className="btn xs"
                      onClick={() => setItemReviewRunId(itemAiRunId)}
                    >
                      Review Results
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn xs subtle"
                    onClick={() => void handleNotificationDismiss(notification)}
                    disabled={isBusy(`notif-dismiss-${notification.id}`)}
                  >
                    {isBusy(`notif-dismiss-${notification.id}`) ? 'Removing...' : 'Dismiss'}
                  </button>
                  {notification.actionUrl ? (
                    <a
                      href={dashboardNotificationActionUrl(notification) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="btn xs"
                    >
                      Open
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTasks = () => {
    if (loading && tasks.items.length === 0) {
      return <div className="text-sm fg-muted">Loading tasks...</div>;
    }
    if (tasks.items.length === 0) {
      return <div className="text-sm fg-muted">No open tasks. Assigned work for your locations will land here.</div>;
    }
    return (
      <div className="stack gap-sm">
        {tasks.items.map((task: DashboardTask) => {
          const keyComplete = `task-complete-${task.id}`;
          const keyReopen = `task-reopen-${task.id}`;
          const keyDismiss = `task-dismiss-${task.id}`;
          const isRecallTask = Boolean(getYearlyRecallRequestId(task.metadata));
          const isEventConfirmationTask = Boolean(getEventConfirmationRequestId(task.metadata));
          const isSuggestionTask = String(task.metadata?.type || '').toLowerCase() === 'suggestion_submitted';
          const suggestionCategoryLabel = isSuggestionTask ? getSuggestionCategoryLabel(task.metadata) : null;
          const keyApproveRecall = `recall-approved-${task.id}`;
          const keyDenyRecall = `recall-denied-${task.id}`;
          const keyApproveConfirmation = `event-confirmation-approved-${task.id}`;
          const keyDenyConfirmation = `event-confirmation-denied-${task.id}`;
          const isCompleted = task.status === 'completed';
          return (
            <div
              key={task.id}
              className="stack gap-xxs"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                background: 'var(--surface)',
              }}
            >
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="stack gap-xxs" style={{ minWidth: 0 }}>
                  <div className="text-sm" style={{ fontWeight: 600 }}>{task.title}</div>
                  {task.description ? (
                    <div className="text-xs fg-muted" style={{ whiteSpace: 'pre-line' }}>{task.description}</div>
                  ) : null}
                </div>
                <span className="pill micro" style={{ background: 'var(--surface-elev)', color: 'var(--text-soft)' }}>
                  {task.priority.toUpperCase()}
                </span>
              </div>

              {isEventConfirmationTask && !isCompleted ? (
                <label className="stack gap-xxs">
                  <span className="text-xxs fg-muted">
                    Decision note (required to deny)
                  </span>
                  <input
                    type="text"
                    className="input"
                    value={confirmationDecisionNotes[task.id] || ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      setConfirmationDecisionNotes((prev) => ({ ...prev, [task.id]: value }));
                    }}
                    placeholder="Add context for the requester"
                    disabled={isBusy(keyApproveConfirmation) || isBusy(keyDenyConfirmation)}
                    maxLength={1000}
                  />
                </label>
              ) : null}

              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="text-xxs fg-muted">
                  {isEventConfirmationTask
                    ? 'Manager approval request'
                    : isSuggestionTask
                      ? suggestionCategoryLabel
                        ? `${suggestionCategoryLabel} submission`
                        : 'Submitted suggestion'
                    : task.taskSource === 'assigned' && task.createdByName
                    ? `Assigned by ${task.createdByName}`
                    : task.locationName
                      ? `Location: ${task.locationName}`
                      : 'Personal task'}
                  {' | Due '}{formatDate(task.dueAt)}
                  {' | Created '}<time dateTime={task.createdAt}>{formatDateTime(task.createdAt)}</time>
                </div>
                <div className="flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  {isEventConfirmationTask && !isCompleted ? (
                    <>
                      <button
                        type="button"
                        className="btn xs"
                        onClick={() => void handleEventConfirmationDecision(task, 'approved')}
                        disabled={isBusy(keyApproveConfirmation) || isBusy(keyDenyConfirmation)}
                      >
                        {isBusy(keyApproveConfirmation) ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn xs danger"
                        onClick={() => void handleEventConfirmationDecision(task, 'denied')}
                        disabled={isBusy(keyApproveConfirmation) || isBusy(keyDenyConfirmation)}
                      >
                        {isBusy(keyDenyConfirmation) ? 'Denying...' : 'Deny'}
                      </button>
                    </>
                  ) : isRecallTask && !isCompleted ? (
                    <>
                      <button
                        type="button"
                        className="btn xs"
                        onClick={() => void handleRecallTaskDecision(task, 'approved')}
                        disabled={isBusy(keyApproveRecall) || isBusy(keyDenyRecall)}
                      >
                        {isBusy(keyApproveRecall) ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn xs danger"
                        onClick={() => void handleRecallTaskDecision(task, 'denied')}
                        disabled={isBusy(keyApproveRecall) || isBusy(keyDenyRecall)}
                      >
                        {isBusy(keyDenyRecall) ? 'Denying...' : 'Deny'}
                      </button>
                    </>
                  ) : !isCompleted && (
                    <button
                      type="button"
                      className="btn xs"
                      onClick={() => void handleTaskComplete(task)}
                      disabled={isBusy(keyComplete) || isBusy(keyDismiss)}
                      title={isSuggestionTask ? 'Mark this suggestion and its task as completed' : 'Mark this task as completed'}
                    >
                      {isBusy(keyComplete) ? 'Completing...' : 'Mark complete'}
                    </button>
                  )}
                  {isCompleted && !isSuggestionTask && (
                    <button
                      type="button"
                      className="btn xs subtle"
                      onClick={() => void handleTaskReopen(task)}
                      disabled={isBusy(keyReopen)}
                    >
                      {isBusy(keyReopen) ? 'Reopening...' : 'Reopen'}
                    </button>
                  )}
                  {!isCompleted ? (
                    <HomeTaskDismissButton
                      disabled={isBusy(keyDismiss) || isBusy(keyComplete) || isBusy(keyApproveRecall) || isBusy(keyDenyRecall) || isBusy(keyApproveConfirmation) || isBusy(keyDenyConfirmation)}
                      onDismiss={(duration) => void handleTaskDismiss(task, duration)}
                    />
                  ) : null}
                  {task.actionUrl ? (
                    <a 
                      href={task.actionUrl} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="btn xs subtle"
                      onClick={(e) => {
                        // Ensure transfer orders open in new tabs
                        if (task.actionUrl?.startsWith('/transfers/')) {
                          e.preventDefault();
                          window.open(task.actionUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      Open
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMentions = () => {
    if (loading && mentions.items.length === 0) {
      return <div className="text-sm fg-muted">Loading chat mentions...</div>;
    }
    if (mentions.items.length === 0) {
      return <div className="text-sm fg-muted">No unread mentions. Teammates' @mentions will show here.</div>;
    }
    async function handleMentionDismiss(mention: DashboardMention) {
      const key = `mention-dismiss-${mention.id}`;
      setBusy(key, true);
      try {
        const error = await updateMentionStatus(
          mention.id,
          { dismissed_at: new Date().toISOString() },
        );
        if (error) throw error;
        updateDashboardData(prev => {
          if (!prev) return prev;
          const items = prev.mentions.items.filter(m => m.id !== mention.id);
          const unread = Math.max(0, (prev.mentions.unread || 0) - (mention.readAt ? 0 : 1));
          return { ...prev, mentions: { unread, items } };
        });
        void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      } catch (err: any) {
        push({ type: 'error', message: err?.message || 'Failed to dismiss mention' });
      } finally {
        setBusy(key, false);
      }
    }

    async function handleMentionSend(mention: DashboardMention) {
      const state = mentionReplyMap[mention.id] || { open: false, text: '' };
      const text = (state.text || '').trim();
      const key = `mention-send-${mention.id}`;
      if (!text) {
        push({ type: 'warn', message: 'Type a reply first.' });
        return;
      }
      setBusy(key, true);
      try {
        const sendError = await sendQuickChatMessage(
          { eventId: mention.eventId, body: text, mentions: [] },
        );
        if (sendError) throw sendError;
        const updateError = await updateMentionStatus(
          mention.id,
          { read_at: new Date().toISOString() },
        );
        if (updateError) throw updateError;
        if (profileId) {
          try {
            await ensureQuoteChatReads([{ messageId: mention.messageId, eventId: mention.eventId, profileId }]);
          } catch (_) { /* noop */ }
        }
        setMentionReplyMap(prev => ({ ...prev, [mention.id]: { open: false, text: '' } }));
        updateDashboardData(prev => {
          if (!prev) return prev;
          const items = prev.mentions.items.filter(m => m.id !== mention.id);
          const unread = Math.max(0, (prev.mentions.unread || 0) - (mention.readAt ? 0 : 1));
          return { ...prev, mentions: { unread, items } };
        });
        void queryClient.invalidateQueries({ queryKey: dashboardQueryKey });
      } catch (err: any) {
        push({ type: 'error', message: err?.message || 'Quick reply failed' });
      } finally {
        setBusy(key, false);
      }
    }

    function setReplyOpen(mentionId: string, open: boolean) {
      setMentionReplyMap(prev => ({ ...prev, [mentionId]: { open, text: prev[mentionId]?.text || '' } }));
    }
    function setReplyText(mentionId: string, text: string) {
      setMentionReplyMap(prev => ({ ...prev, [mentionId]: { open: true, text } }));
    }
    function onMentionReplyChange(mentionId: string) {
      return (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReplyText(mentionId, e.target.value);
      };
    }
    return (
      <div className="stack gap-sm">
        {mentions.items.map((mention: DashboardMention) => {
          const eventTarget = mention.event?.id ? `/events/${mention.event.id}/order?tab=chat` : undefined;
          const replyState = mentionReplyMap[mention.id] || { open: false, text: '' };
          return (
            <div
              key={mention.id}
              className="stack gap-xxs"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                background: 'var(--surface-soft)',
              }}
            >
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="stack gap-xxs">
                  <div className="text-sm" style={{ fontWeight: 600 }}>{mention.authorName || 'Someone mentioned you'}</div>
                  <div className="text-xs fg-muted">{summariseBody(mention.body, 200)}</div>
                </div>
                <span className="pill micro" style={{ background: 'var(--info-bg)', color: 'var(--info-fg)' }}>
                  {mention.readAt ? 'Read' : 'Unread'}
                </span>
              </div>
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="text-xxs fg-muted">
                  {mention.event?.name || mention.event?.code || 'Event'} | {formatDateTime(mention.createdAt)}
                </div>
                <div className="flex" style={{ gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn xs subtle"
                    onClick={() => void handleMentionDismiss(mention)}
                    disabled={isBusy(`mention-dismiss-${mention.id}`)}
                  >
                    {isBusy(`mention-dismiss-${mention.id}`) ? 'Removing...' : 'Dismiss'}
                  </button>
                  {!replyState.open && (
                    <button type="button" className="btn xs subtle" onClick={() => setReplyOpen(mention.id, true)}>
                      Quick Reply
                    </button>
                  )}
                  {eventTarget ? (
                    <Link to={eventTarget} className="btn xs">Go To Event</Link>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTransfers = () => {
    if (loading && transfers.items.length === 0) {
      return <div className="text-sm fg-muted">Loading transfer requests...</div>;
    }
    if (transfers.items.length === 0) {
      return <div className="text-sm fg-muted">No transfer requests require action right now.</div>;
    }
    return (
      <div className="stack gap-sm">
        {transfers.items.map((request: DashboardTransferRequest) => {
          const approveKey = `transfer-approve-${request.id}`;
          const rejectKey = `transfer-reject-${request.id}`;
          return (
            <div
              key={request.id}
              className="stack gap-xxs"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                background: 'var(--surface)',
              }}
            >
              <div className="stack gap-xxs">
                <div className="text-sm" style={{ fontWeight: 600 }}>
                  {request.event?.name || request.event?.code || 'Transfer request'}
                </div>
                <div className="text-xs fg-muted">
                  {request.item?.name || 'Item'} | Qty {request.qty ?? '--'} | Needed {formatDate(request.neededBy)}
                </div>
                <div className="text-xs fg-muted">
                  {'From '}
                  {request.fromLocation?.name || '--'}
                  {' -> To '}
                  {request.toLocation?.name || '--'}
                </div>
              </div>
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="text-xxs fg-muted">Expected return {formatDate(request.expectedReturn)}</div>
                <div className="flex" style={{ gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn xs"
                    onClick={() => void handleTransferDecision(request, 'approve')}
                    disabled={isBusy(approveKey)}
                  >
                    {isBusy(approveKey) ? 'Approving...' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="btn xs subtle"
                    onClick={() => void handleTransferDecision(request, 'reject')}
                    disabled={isBusy(rejectKey)}
                  >
                    {isBusy(rejectKey) ? 'Rejecting...' : 'Reject'}
                  </button>
                  {request.event?.id ? (
                    <Link to={`/events/${request.event.id}/order?tab=items`} className="btn xs subtle">
                      View order
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const resolveConversationName = (conv: ConversationRow): string => {
    if (conv.conversation_type === 'group') return conv.title || 'Group Chat';
    const participants = chatParticipantsMap[conv.conversation_id] || [];
    const other = participants.find((p) => p.id !== profileId);
    if (other) {
      const full = other.full_name?.trim();
      if (full) return full;
      const combined = `${other.first_name || ''} ${other.last_name || ''}`.trim();
      if (combined) return combined;
      return other.email || 'Direct Message';
    }
    return 'Direct Message';
  };

  const getConversationOpenTarget = React.useCallback((conv: ConversationRow): string | null => {
    if (!conv.entity_id) return null;
    if (conv.conversation_type === 'event') return `/events/${conv.entity_id}/order?tab=chat`;
    if (conv.conversation_type === 'transfer') return `/transfers/${conv.entity_id}/chat`;
    return null;
  }, []);

  const openConversationInWidget = React.useCallback((conversationId: string) => {
    try {
      window.dispatchEvent(
        new CustomEvent('chat:open-conversation', {
          detail: { conversationId, openPanel: true },
        }),
      );
    } catch {
      // no-op
    }
  }, []);

  const handleUnreadConversationOpen = React.useCallback(
    (conv: ConversationRow) => {
      const target = getConversationOpenTarget(conv);
      if (target) {
        navigate(target);
        return;
      }
      openConversationInWidget(conv.conversation_id);
    },
    [getConversationOpenTarget, navigate, openConversationInWidget],
  );

  const handleConversationReplySend = React.useCallback(
    async (conv: ConversationRow) => {
      const key = `chat-reply-send-${conv.conversation_id}`;
      const replyState = conversationReplyMap[conv.conversation_id] || { open: false, text: '' };
      const text = replyState.text.trim();
      if (!text) {
        push({ type: 'warn', message: 'Type a reply first.' });
        return;
      }
      setBusy(key, true);
      try {
        const sendError = await sendConversationMessage({
          conversationId: conv.conversation_id,
          body: text,
          attachments: [],
          mentions: [],
          authorDisplayName: chatDisplayName,
          userId: session?.user?.id ?? null,
        });
        if (sendError) throw sendError;
        setConversationReplyMap((prev) => ({ ...prev, [conv.conversation_id]: { open: false, text: '' } }));
        markConversationRead(conv.conversation_id);
      } catch (err: any) {
        push({ type: 'error', message: err?.message || 'Quick reply failed' });
      } finally {
        setBusy(key, false);
      }
    },
    [chatDisplayName, conversationReplyMap, markConversationRead, push, session?.user?.id, setBusy],
  );

  const renderUnreadMessages = () => {
    const unreadConversations = chatConversations.filter(
      (c) => (chatCounts[c.conversation_id] ?? 0) > 0,
    );
    if (unreadConversations.length === 0) {
      return <div className="text-sm fg-muted">No unread messages. New conversations will appear here.</div>;
    }
    return (
      <div className="stack gap-sm">
        {unreadConversations.map((conv) => {
          const count = chatCounts[conv.conversation_id] ?? 0;
          const conversationTitle = resolveConversationName(conv);
          const isGroup = conv.conversation_type === 'group';
          const participants = chatParticipantsMap[conv.conversation_id] || [];
          const replyState = conversationReplyMap[conv.conversation_id] || { open: false, text: '' };
          const openTarget = getConversationOpenTarget(conv);
          const openLabel = openTarget ? 'Open Chat Tab' : 'Open in Chat';
          const kindLabel =
            conv.conversation_type === 'group'
              ? 'Group'
              : conv.conversation_type === 'direct'
                ? 'Direct'
                : conv.conversation_type === 'event'
                  ? 'Event'
                  : 'Transfer';
          return (
            <div
              key={conv.conversation_id}
              className="stack gap-xxs"
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '0.85rem 1rem',
                background: 'var(--surface-soft)',
              }}
            >
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="stack gap-xxs" style={{ minWidth: 0 }}>
                  <div className="text-sm" style={{ fontWeight: 600 }}>{conversationTitle}</div>
                  {isGroup && participants.length > 0 && (
                    <div className="text-xs fg-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {participants
                        .filter((p) => p.id !== profileId)
                        .map((p) => p.first_name || p.full_name?.split(' ')[0] || p.email || 'Unknown')
                        .join(', ')}
                    </div>
                  )}
                </div>
                <span
                  className="pill micro"
                  style={{
                    background: 'var(--danger-bg)',
                    color: 'var(--danger-fg)',
                    fontWeight: 600,
                  }}
                >
                  {count} unread
                </span>
              </div>
              <div className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div className="text-xxs fg-muted">
                  {kindLabel} · Last activity {conv.last_message_at ? formatDateTime(conv.last_message_at) : formatDateTime(conv.updated_at)}
                </div>
                <div className="flex" style={{ gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn xs subtle"
                    style={{ fontSize: '.7rem', padding: '.2rem .5rem' }}
                    onClick={() => dismissConversation(conv.conversation_id)}
                  >
                    Dismiss
                  </button>
                  {!replyState.open && (
                    <button
                      type="button"
                      className="btn xs subtle"
                      style={{ fontSize: '.7rem', padding: '.2rem .5rem' }}
                      onClick={() => {
                        setConversationReplyMap((prev) => ({
                          ...prev,
                          [conv.conversation_id]: { open: true, text: prev[conv.conversation_id]?.text ?? '' },
                        }));
                      }}
                    >
                      Quick Reply
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn xs"
                    style={{ fontSize: '.7rem', padding: '.2rem .5rem' }}
                    onClick={() => handleUnreadConversationOpen(conv)}
                  >
                    {openLabel}
                  </button>
                </div>
              </div>
              {replyState.open && (
                <div className="stack gap-xxs" style={{ marginTop: '0.25rem' }}>
                  <textarea
                    className="control-sm"
                    rows={2}
                    placeholder="Reply..."
                    value={replyState.text}
                    onChange={(event) => {
                      const nextText = event.target.value;
                      setConversationReplyMap((prev) => ({
                        ...prev,
                        [conv.conversation_id]: { open: true, text: nextText },
                      }));
                    }}
                  />
                  <div className="flex" style={{ gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn xs subtle"
                      onClick={() => {
                        setConversationReplyMap((prev) => ({
                          ...prev,
                          [conv.conversation_id]: { open: false, text: '' },
                        }));
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn xs"
                      onClick={() => void handleConversationReplySend(conv)}
                      disabled={isBusy(`chat-reply-send-${conv.conversation_id}`)}
                    >
                      {isBusy(`chat-reply-send-${conv.conversation_id}`) ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="home-page stack gap-xl">
      {/* Quick Actions (restored) - moved below */}
      {false && (
      <div className="section-shell stack gap-sm home-hero-card">
        <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <div className="stack gap-xxs">
            <h2 className="h3 m0">Quick Actions</h2>
            <p className="text-sm fg-muted m0">Jump straight into common workflows.</p>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Approvals</h2>
                <p className="text-xs fg-muted m0">{approvalsCount} quote approvals assigned to you.</p>
              </div>
            </header>
            <div className="stack gap-xs">
              {approvalsError ? (
                <div className="alert alert-error" role="alert">{approvalsError}</div>
              ) : null}
              {approvalsLoading && approvals.length === 0 && !approvalsError ? (
                <div className="text-sm fg-muted">Loading approvals...</div>
              ) : null}
              {approvals.length === 0 && !approvalsLoading && !approvalsError ? (
                <div className="text-sm fg-muted">No approvals need your decision right now.</div>
              ) : null}
              {approvals.map(assignment => {
                const target = `/events/${assignment.eventId}/order?tab=approvals`;
                const requestedLabel = assignment.requestedAt
                  ? `Requested ${new Date(assignment.requestedAt).toLocaleString()}`
                  : 'Request time unavailable';
                const totalLabel = assignment.amount != null
                  ? `${new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:2}).format(assignment.amount)}${assignment.basis === 'pre_discount' ? ' (pre-discount)' : ''}`
                  : 'Total unavailable';
                return (
                  <div
                    key={assignment.assignmentId}
                    className="pending-approval-card"
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '0.9rem 1rem',
                      background: 'var(--surface-soft)',
                      display: 'grid',
                      gap: '0.35rem',
                    }}
                  >
                    <Link
                      to={target}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'grid',
                        gap: '0.35rem',
                      }}
                    >
                      <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <div className="text-sm" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {assignment.eventName || 'Untitled Event'}
                          </div>
                          <div className="text-xs fg-muted">
                            {assignment.eventCode ? `Event code ${assignment.eventCode}` : 'Order approval pending'}
                          </div>
                        </div>
                        <span className="pill micro" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
                          Pending
                        </span>
                      </div>
                      <div className="text-xs fg-muted" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span>{assignment.policyName || 'Order approvals'}</span>
                        <span>{totalLabel}</span>
                      </div>
                      <div className="text-xxs fg-muted">{requestedLabel}</div>
                    </Link>
                    <div style={{ marginTop: 4 }}>
                      <button
                        type="button"
                        className="btn xs subtle"
                        onClick={async (e) => {
                          e.preventDefault();
                          const key = `dismiss-${assignment.assignmentId}`;
                          if (pendingMap[key]) return;
                          setPendingMap(m => ({ ...m, [key]: true }));
                          try {
                            const err = await dismissApprovalAssignment(assignment.assignmentId);
                            if (err) throw err;
                            await queryClient.invalidateQueries({ queryKey: pendingApprovalsQueryKey(profile?.id) });
                            push({ type: 'success', message: 'Dismissed for today' });
                          } catch (err: any) {
                            push({ type: 'error', message: err?.message ?? 'Failed to dismiss' });
                          } finally {
                            setPendingMap(m => ({ ...m, [key]: false }));
                          }
                        }}
                        disabled={pendingMap[`dismiss-${assignment.assignmentId}`]}
                      >
                        {pendingMap[`dismiss-${assignment.assignmentId}`] ? 'Dismissing…' : 'Dismiss for today'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <Link to="/events?create=1" className="section-shell" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xxs fg-muted">Create</span>
              <h3 className="m0">New Event</h3>
              <p className="text-sm fg-muted m0">Start a site survey for an upcoming event.</p>
            </div>
          </Link>
          <Link to="/venues?ai=1" className="section-shell" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xxs fg-muted">Add</span>
              <h3 className="m0">Add a Venue using AI</h3>
              <p className="text-sm fg-muted m0">Generate venue details from a name.</p>
            </div>
          </Link>
          {isAdmin ? (
            <Link to="/admin/users" className="section-shell" style={{ textDecoration: 'none' }}>
              <div className="stack gap-xxs">
                <span className="text-xxs fg-muted">Manage</span>
                <h3 className="m0">Users & Roles</h3>
                <p className="text-sm fg-muted m0">Invite users and assign permissions.</p>
              </div>
            </Link>
          ) : null}
          {lastEvent ? (
            <Link to={`/events/${lastEvent?.id}/overview`} className="section-shell" style={{ textDecoration: 'none' }}>
              <div className="stack gap-xxs">
                <span className="text-xxs fg-muted">Last Viewed</span>
                <h3 className="m0">{lastEvent?.name || 'Untitled Event'}</h3>
                <p className="text-sm fg-muted m0">{lastEvent?.event_code ? `Code: ${lastEvent?.event_code}` : 'Open the last event you viewed.'}</p>
              </div>
            </Link>
          ) : null}
        </div>
      </div>
      )}
      <div ref={homeOverviewGuideRef} className="section-shell stack gap-sm guide-scope">
        <GuideQuestionButton
          ref={homeGuideButtonRef}
          onClick={homeGuide.start}
          disabled={!homeGuide.canLaunch}
          title="Open Home guide"
          ariaLabel="Open Home guide"
        />
        <div className="home-overview-header">
          <div className="stack gap-xxs home-overview-copy">
            <h1 className="m0">
              {greeting}, <span className="home-overview-name">{displayName}</span>.
            </h1>
            <p className="text-sm fg-muted m0">Stay on top of notifications, tasks, approvals, chat mentions, and transfer requests.</p>
            {HomeGlance ? (
              <LayoutFeature name="homeGlance">
                <HomeGlance
                  unreadNotifications={countUnreadNotifications(notifications.items)}
                  openTasks={tasks.total}
                  pendingApprovals={quoteApprovalsCount}
                />
              </LayoutFeature>
            ) : null}
          </div>
          <div ref={homeSimpleInOutGuideRef} className="home-simple-inout-guide-target home-status-controls">
            <SimpleInOutStatusCard />
            <div className="home-status-actions">
              <SimpleInOutDotToggle />
              {canViewSchedule ? (
                <Link className="btn btn-primary home-status-actions__schedule" to="/schedule">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" />
                  </svg>
                  View Schedule
                </Link>
              ) : null}
              <JobTimeButton home />
            </div>
          </div>
        </div>
        {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
      </div>
      {/* Workspace stats */}
      <div ref={homeStatsGuideRef} className="section-shell home-stats-shell">
        <div className="home-stats-grid">
          {canViewEvents ? <div className="home-stat-card home-stat-card--events">
            <div className="stack gap-xxs" style={{ flex: 1 }}>
              <div className="home-stat-card__head">
                <span className="text-xs fg-muted home-stat-card__label">Events</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ padding: '0.25rem', lineHeight: 1, minWidth: 0, borderRadius: 6 }}
                  title="Event Leaderboard"
                  aria-label="Event Leaderboard"
                  onClick={() => setShowLeaderboard(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
                </button>
              </div>
              <span className="home-stat-card__value">{eventCountsLoading ? '...' : (eventCounts?.total ?? 0).toLocaleString()}</span>
              <div className="home-stat-card__breakdown">
                <div>
                  <span className="text-xs fg-muted home-stat-card__meta-label">Upcoming</span>
                  <span className="text-sm home-stat-card__meta-value">{eventCountsLoading ? '...' : (eventCounts?.upcoming ?? 0).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-xs fg-muted home-stat-card__meta-label">In Progress</span>
                  <span className="text-sm home-stat-card__meta-value">{eventCountsLoading ? '...' : (eventCounts?.inProgress ?? 0).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-xs fg-muted home-stat-card__meta-label">Completed</span>
                  <span className="text-sm home-stat-card__meta-value">{eventCountsLoading ? '...' : (eventCounts?.completed ?? 0).toLocaleString()}</span>
                </div>
              </div>
              <Link to="/events" className="text-sm home-stat-card__cta" aria-label="View events">
                {'View >'}
              </Link>
            </div>
          </div> : null}
          {canViewCustomers ? <div className="stack gap-xxs home-stat-card">
            <span className="text-xs fg-muted home-stat-card__label">Customers</span>
            <span className="home-stat-card__value">{statsLoading ? '...' : stats.customers.toLocaleString()}</span>
            <Link to="/clients" className="text-sm home-stat-card__cta" aria-label="Manage customers">
              {'Manage >'}
            </Link>
          </div> : null}
          {canViewVenues ? <div className="stack gap-xxs home-stat-card">
            <span className="text-xs fg-muted home-stat-card__label">Venues</span>
            <span className="home-stat-card__value">{statsLoading ? '...' : stats.venues.toLocaleString()}</span>
            <Link to="/venues" className="text-sm home-stat-card__cta" aria-label="Browse venues">
              {'Browse >'}
            </Link>
          </div> : null}
          {canViewUsers ? (
            <div className="stack gap-xxs home-stat-card">
              <span className="text-xs fg-muted home-stat-card__label">Users</span>
              <span className="home-stat-card__value">{statsLoading ? '...' : stats.users.toLocaleString()}</span>
              <Link to="/admin/users" className="text-sm home-stat-card__cta" aria-label="Manage users">
                {'Manage >'}
              </Link>
            </div>
          ) : null}
        </div>
      </div>
      
      <div className="stack gap-xl">
        <div ref={homeAttentionGuideRef} className="home-cards-grid">
          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Approvals</h2>
                <p className="text-xs fg-muted m0">{quoteApprovalsCount} quote approvals assigned to you.</p>
              </div>
            </header>
            <div className="stack gap-xs">
              {quoteApprovalsError ? (
                <div className="alert alert-error" role="alert">{quoteApprovalsError}</div>
              ) : null}
              {quoteApprovalsLoading && quoteApprovalsShown === 0 && !quoteApprovalsError ? (
                <div className="text-sm fg-muted">Loading approvals...</div>
              ) : null}
              {quoteApprovalsShown === 0 && !quoteApprovalsLoading && !quoteApprovalsError ? (
                <div className="text-sm fg-muted">No approvals need your decision right now.</div>
              ) : null}
              {approvals.map(assignment => {
                const target = `/events/${assignment.eventId}/order?tab=approvals`;
                const requestedLabel = assignment.requestedAt
                  ? `Requested ${new Date(assignment.requestedAt).toLocaleString()}`
                  : 'Request time unavailable';
                const totalLabel = assignment.amount != null
                  ? `${new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:2}).format(assignment.amount)}${assignment.basis === 'pre_discount' ? ' (pre-discount)' : ''}`
                  : 'Total unavailable';
                return (
                  <PendingApprovalCard
                    key={assignment.assignmentId}
                    to={target}
                    title={assignment.eventName || 'Untitled Event'}
                    subtitle={assignment.eventCode ? `Event code ${assignment.eventCode}` : 'Order approval pending'}
                    pillLabel="Pending"
                    detail={assignment.policyName || 'Order approvals'}
                    amountLabel={totalLabel}
                    requestedLabel={requestedLabel}
                  >
                    <button
                      type="button"
                      className="btn xs subtle"
                      onClick={async (e) => {
                        e.preventDefault();
                        const key = `dismiss-${assignment.assignmentId}`;
                        if (pendingMap[key]) return;
                        setPendingMap(m => ({ ...m, [key]: true }));
                        try {
                          const err = await dismissApprovalAssignment(assignment.assignmentId);
                          if (err) throw err;
                          await queryClient.invalidateQueries({ queryKey: pendingApprovalsQueryKey(profile?.id) });
                          push({ type: 'success', message: 'Dismissed for today' });
                        } catch (err: any) {
                          push({ type: 'error', message: err?.message ?? 'Failed to dismiss' });
                        } finally {
                          setPendingMap(m => ({ ...m, [key]: false }));
                        }
                      }}
                      disabled={pendingMap[`dismiss-${assignment.assignmentId}`]}
                    >
                      {pendingMap[`dismiss-${assignment.assignmentId}`] ? 'Dismissing…' : 'Dismiss for today'}
                    </button>
                  </PendingApprovalCard>
                );
              })}
              {agreementApprovals.map(item => (
                <PendingApprovalCard
                  key={`agreement-${item.quoteId}`}
                  to={`/service-agreements/quote/${item.quoteId}`}
                  title={`${item.clientName || 'Customer'} · ${item.tierName || 'Service'} agreement`}
                  subtitle={`Service agreement quote · Version ${item.version}`}
                  pillLabel={item.override ? 'Needs reason' : 'Pending'}
                  detail={item.override ? 'No approval policy · any manager can approve' : item.policyName || 'Service agreement approvals'}
                  amountLabel={item.price != null
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(item.price)
                    : 'Total unavailable'}
                  requestedLabel={item.requestedAt ? `Requested ${new Date(item.requestedAt).toLocaleString()}` : 'Request time unavailable'}
                />
              ))}
            </div>
          </section>
          {canSeeDeepApprovals && deepRequests.length > 0 && (
            <section className="section-shell stack gap-sm">
              <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <div>
                  <h2 className="h4 m0">AI Research Approvals</h2>
                  <p className="text-xs fg-muted m0">
                    {deepRequests.length} deep research request{deepRequests.length !== 1 ? 's' : ''} awaiting approval.
                  </p>
                </div>
              </header>
              <div className="stack gap-xs">
                {deepRequests.map((req: DeepResearchRequest) => {
                  const approveKey = `deep-approve-${req.job_id}`;
                  const denyKey = `deep-deny-${req.job_id}`;
                  const busy = pendingMap[approveKey] || pendingMap[denyKey];
                  return (
                    <div
                      key={req.job_id}
                      className="pending-approval-card"
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        padding: '0.9rem 1rem',
                        background: 'var(--surface-soft)',
                        display: 'grid',
                        gap: '0.35rem',
                      }}
                    >
                      <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <div className="text-sm" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {req.venue_query}
                          </div>
                          <div className="text-xs fg-muted">
                            Deep research requires approval to proceed
                          </div>
                        </div>
                        <span className="pill micro" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
                          Pending
                        </span>
                      </div>
                      {req.deep_research_requested_at && (
                        <div className="text-xxs fg-muted">
                          Requested {new Date(req.deep_research_requested_at).toLocaleString()}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: 4 }}>
                        <button
                          type="button"
                          className="btn xs subtle"
                          onClick={() => void handleDenyDeep(req.job_id)}
                          disabled={!!busy}
                        >
                          {pendingMap[denyKey] ? 'Denying…' : 'Deny'}
                        </button>
                        <button
                          type="button"
                          className="btn xs"
                          onClick={() => void handleApproveDeep(req.job_id)}
                          disabled={!!busy}
                        >
                          {pendingMap[approveKey] ? 'Approving…' : 'Approve'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Notifications</h2>
                <p className="text-xs fg-muted m0">
                  {notifications.total + broadcasts.length} total assigned to you or your locations
                  {broadcasts.length > 0 ? ` (${broadcasts.length} broadcast${broadcasts.length === 1 ? '' : 's'})` : ''}.
                </p>
              </div>
            </header>
            {renderNotifications()}
          </section>

          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Tasks</h2>
                <p className="text-xs fg-muted m0">{tasks.total} open to-dos waiting for your team.</p>
              </div>
              <Link to="/todo" className="btn xs subtle">View all</Link>
            </header>
            {renderTasks()}
          </section>
        </div>

        <div className="home-cards-grid">
          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Unread Messages</h2>
                <p className="text-xs fg-muted m0">
                  {chatUnread > 0
                    ? `${chatUnread} unread message${chatUnread === 1 ? '' : 's'} across your conversations.`
                    : 'No unread messages.'}
                </p>
              </div>
              {chatUnread > 0 && (
                <button
                  type="button"
                  className="btn xs subtle"
                  onClick={() => dismissAllConversations()}
                >
                  Dismiss All
                </button>
              )}
            </header>
            {renderUnreadMessages()}
          </section>

          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Chat mentions</h2>
                <p className="text-xs fg-muted m0">{mentions.unread} unread @mentions across your events.</p>
              </div>
            </header>
            {renderMentions()}
          </section>
        </div>

        <div className="home-cards-grid">
          <section className="section-shell stack gap-sm">
            <header className="flex between center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <div>
                <h2 className="h4 m0">Transfer requests</h2>
                <p className="text-xs fg-muted m0">{transfers.pending} pending decisions for your locations.</p>
              </div>
            </header>
            {renderTransfers()}
          </section>
        </div>
      </div>
      {/* Quick Actions (moved to bottom) */}
      <div ref={homeQuickActionsGuideRef} className="section-shell stack gap-sm home-quick-actions">
        <div className="flex between center" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
          <div className="stack gap-xxs">
            <h2 className="h3 m0">Quick Actions</h2>
            <p className="text-sm fg-muted m0">Jump straight into common workflows.</p>
          </div>
        </div>
        <div className="home-quick-actions-grid">
          {canUseInventoryTools ? <button
            type="button"
            className="section-shell home-quick-action-card home-quick-action-button"
            onClick={() => setShowPriceAvailabilityModal(true)}
          >
            <div className="stack gap-xxs">
              <span className="text-xs fg-muted">Check</span>
              <h3 className="m0">Price & Availability</h3>
              <p className="text-sm fg-muted m0">Quickly price items, check stock, and estimate delivery.</p>
            </div>
          </button> : null}
          {canEditPage('events') ? <Link to="/events?create=1" className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xs fg-muted">Create</span>
              <h3 className="m0">New Event</h3>
              <p className="text-sm fg-muted m0">Start a site survey for an upcoming event.</p>
            </div>
          </Link> : null}
          {canEditFeature('signatures.library', 'own') ? <Link to="/signature-documents?upload=1" className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xs fg-muted">E-signature</span>
              <h3 className="m0">Send PDF for Signature</h3>
              <p className="text-sm fg-muted m0">Upload a PDF, place fields, and create a public signing link.</p>
            </div>
          </Link> : null}
          {canEditPage('venues') ? <Link to="/venues?ai=1" className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xs fg-muted">Add</span>
              <h3 className="m0">Add a Venue using AI</h3>
              <p className="text-sm fg-muted m0">Generate venue details from a name.</p>
            </div>
          </Link> : null}
          {isAdmin ? (
            <Link to="/admin/users" className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
              <div className="stack gap-xxs">
                <span className="text-xs fg-muted">Manage</span>
                <h3 className="m0">Users & Roles</h3>
                <p className="text-sm fg-muted m0">Invite users and assign permissions.</p>
              </div>
            </Link>
          ) : null}
          {canViewEvents && lastEvent ? (
            <Link to={`/events/${lastEvent.id}/overview`} className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
              <div className="stack gap-xxs">
                <span className="text-xs fg-muted">Last Viewed</span>
                <h3 className="m0">{lastEvent.name || 'Untitled Event'}</h3>
                <p className="text-sm fg-muted m0">{lastEvent.event_code ? `Code: ${lastEvent.event_code}` : 'Open the last event you viewed.'}</p>
              </div>
            </Link>
          ) : null}
          {canUseServiceScan ? <Link to="/service/scan" className="section-shell home-quick-action-card home-quick-action-link" style={{ textDecoration: 'none' }}>
            <div className="stack gap-xxs">
              <span className="text-xs fg-muted">Service</span>
              <h3 className="m0">Flag for Service</h3>
              <p className="text-sm fg-muted m0">Scan a barcode to create a service ticket.</p>
            </div>
          </Link> : null}
        </div>
      </div>
      {canViewEvents && showLeaderboard && (
        <React.Suspense fallback={null}>
          <EventLeaderboardModal
            rows={eventCounts?.byLocation ?? []}
            onClose={() => setShowLeaderboard(false)}
          />
        </React.Suspense>
      )}
      {showPriceAvailabilityModal ? (
        <React.Suspense fallback={null}>
          <HomePriceAvailabilityModal
            open={showPriceAvailabilityModal}
            onClose={() => setShowPriceAvailabilityModal(false)}
          />
        </React.Suspense>
      ) : null}
      <HomeJobCaptureAction canUseInventoryTools={canUseInventoryTools} />
      {itemReviewRunId && (
        <ItemAiLookupReviewModal
          runId={itemReviewRunId}
          onClose={() => setItemReviewRunId(null)}
        />
      )}
      {homeGuide.open && homeGuide.activeStep ? (
        <GuideTourOverlay
          activeStep={homeGuide.activeStep}
          ariaLabel="Home guided tour"
          isFinalStep={homeGuide.isFinalStep}
          onClose={homeGuide.close}
          onNext={homeGuide.next}
          onPrev={homeGuide.prev}
          spotlightRect={homeGuide.spotlightRect}
          stepCountLabel={homeGuide.stepCountLabel}
          stepIndex={homeGuide.stepIndex}
        />
      ) : null}
    </div>
  );
}
